// OrderReviewPanel — pre-confirmation order summary with line items, tax, total.
// Inline styles only — no Tailwind. Themed via useEmbedTokens(); the totals
// block is wrapped in an EmbedCard. RESTYLE ONLY: the order math, currency
// formatting, address formatting, and onConfirm/onBack wiring are unchanged.

import {
  useEmbedTokens,
  EmbedCard,
  EmbedButton,
} from "../../embed-shared/primitives";
import { formatCurrency } from "../../embed-shared/currency";
import { TAX_AT_BILLING_VALUE, TOTAL_BEFORE_TAX_LABEL, isTaxPending } from "../../embed-shared/tax-display";
import type { CartLine } from "../../embed-shared/types";
import type { AddressWire } from "./address-api";
import { useStorefrontConfig } from "./storefront-config";

export interface OrderReviewPanelProps {
  lines: CartLine[];
  subtotal: number;
  taxEstimate: number;
  promoDiscount: number;
  shipping: number;
  total: number;
  promoCode?: string | null;
  shippingAddress?: AddressWire | null;
  billingAddress?: AddressWire | null;
  paymentSummary?: string;
  onConfirm: () => void;
  onBack?: () => void;
  confirming?: boolean;
  /** Extra reason to block confirm without the "Placing order..." label
   *  (e.g. the cart behind the panel is reloading or failed to load). */
  confirmDisabled?: boolean;
}

function formatAddress(addr: AddressWire): string {
  const parts = [addr.line1];
  if (addr.line2) parts.push(addr.line2);
  parts.push(`${addr.city}, ${addr.state} ${addr.postal_code}${addr.country_code && addr.country_code !== "US" ? ` ${addr.country_code}` : ""}`);
  return parts.join(", ");
}

export function OrderReviewPanel({
  lines,
  subtotal,
  taxEstimate,
  promoDiscount,
  shipping,
  total,
  promoCode,
  shippingAddress,
  billingAddress,
  paymentSummary,
  onConfirm,
  onBack,
  confirming,
  confirmDisabled,
}: OrderReviewPanelProps) {
  const t = useEmbedTokens();
  // TD-308: shipping=$0 label is operator-configurable (default "$0.00" since
  // migration 144). D6: the $0 value renders in the neutral text token, not
  // the success green — a green $0.00 reads as a promotion.
  // TD-521: display currency is the marketplace-wide settings value — this
  // panel dropped its currency prop (fed by a never-populated cart.currency)
  // in favour of the same context every other money format reads.
  const { currency, freeShippingLabel } = useStorefrontConfig();

  const sectionHeading = {
    fontSize: t.fontSize.sm,
    fontWeight: 600,
    color: t.colors.textSecondary,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.lg }}>
      <h3
        style={{
          fontSize: t.fontSize.lg,
          fontWeight: 600,
          color: t.colors.text,
          margin: 0,
        }}
      >
        Review your order
      </h3>

      {/* Line items */}
      <div>
        <h4 style={{ ...sectionHeading, margin: `0 0 ${t.spacing.sm}px 0` }}>
          Items ({lines.length})
        </h4>
        {lines.map((line) => (
          <div
            key={line.id}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: `${t.spacing.xs}px 0`,
              fontSize: t.fontSize.sm,
            }}
          >
            <span style={{ color: t.colors.text }}>
              {line.product_name} x{line.quantity}
            </span>
            <span style={{ fontWeight: 600, color: t.colors.text, fontVariantNumeric: "tabular-nums" }}>
              {formatCurrency(line.line_total, currency)}
            </span>
          </div>
        ))}
      </div>

      {/* Addresses */}
      {shippingAddress && (
        <div>
          <h4 style={{ ...sectionHeading, margin: `0 0 ${t.spacing.xs}px 0` }}>
            Shipping address
          </h4>
          <p style={{ fontSize: t.fontSize.sm, color: t.colors.text, margin: 0 }}>
            {formatAddress(shippingAddress)}
          </p>
        </div>
      )}
      {billingAddress && (
        <div>
          <h4 style={{ ...sectionHeading, margin: `0 0 ${t.spacing.xs}px 0` }}>
            Billing address
          </h4>
          <p style={{ fontSize: t.fontSize.sm, color: t.colors.text, margin: 0 }}>
            {formatAddress(billingAddress)}
          </p>
        </div>
      )}

      {/* Payment */}
      {paymentSummary && (
        <div>
          <h4 style={{ ...sectionHeading, margin: `0 0 ${t.spacing.xs}px 0` }}>
            Payment
          </h4>
          <p style={{ fontSize: t.fontSize.sm, color: t.colors.text, margin: 0 }}>
            {paymentSummary}
          </p>
        </div>
      )}

      {/* Totals — mock .summary-row: regular rows read as secondary text,
          values are tabular; the total row alone carries primary bold text. */}
      <EmbedCard padding={t.spacing.md}>
        <div style={{ color: t.colors.text }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: t.fontSize.sm, color: t.colors.textSecondary, padding: `${t.spacing.xs}px 0` }}>
            <span>Subtotal</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatCurrency(subtotal, currency)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: t.fontSize.sm, color: t.colors.textSecondary, padding: `${t.spacing.xs}px 0` }}>
            <span>{isTaxPending(taxEstimate) ? "Tax" : "Tax (estimate)"}</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{isTaxPending(taxEstimate) ? TAX_AT_BILLING_VALUE : formatCurrency(taxEstimate, currency)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: t.fontSize.sm, color: t.colors.textSecondary, padding: `${t.spacing.xs}px 0` }}>
            <span>Shipping</span>
            <span style={{ ...(shipping === 0 ? { color: t.colors.text, fontWeight: 600 } : {}), fontVariantNumeric: "tabular-nums" }}>
              {shipping === 0 ? freeShippingLabel : formatCurrency(shipping, currency)}
            </span>
          </div>
          {promoDiscount > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: t.fontSize.sm, padding: `${t.spacing.xs}px 0`, color: t.colors.successGreen }}>
              <span>{promoCode ? `Promo (${promoCode})` : "Promo discount"}</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{`-${formatCurrency(promoDiscount, currency)}`}</span>
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: t.fontSize.md,
              fontWeight: 700,
              padding: `${t.spacing.sm}px 0 0 0`,
              borderTop: `1px solid ${t.colors.cardBorder}`,
              marginTop: t.spacing.sm,
            }}
          >
            <span>{isTaxPending(taxEstimate) ? TOTAL_BEFORE_TAX_LABEL : "Total"}</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatCurrency(total, currency)}</span>
          </div>
        </div>
      </EmbedCard>

      {/* Actions */}
      <div style={{ display: "flex", gap: t.spacing.md }}>
        {onBack && (
          <EmbedButton type="button" variant="secondary" onClick={onBack}>
            Back
          </EmbedButton>
        )}
        <EmbedButton
          type="button"
          variant="primary"
          fullWidth
          onClick={onConfirm}
          disabled={confirming || confirmDisabled}
          loading={confirming}
        >
          {confirming ? "Placing order..." : "Place order"}
        </EmbedButton>
      </div>
    </div>
  );
}
